import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  BottomDrawer,
  Button,
  DataTable,
  EmptyState,
  FormField,
  Group,
  Loader,
  NumberInput,
  PageHeader,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@vipso/shared';
import {
  useGetTaskTemplatesQuery,
  useUpsertTaskTemplateMutation,
  type TaskTemplate,
} from '../api/adminApi';

type TemplateForm = {
  id?: string;
  code: string;
  title: string;
  description: string;
  category: string;
  kind: string;
  assigneeType: string;
  assigneeRoleKey: string;
  priority: string;
  actionKey: string;
  sortOrder: number;
  isActive: boolean;
};

const EMPTY_FORM: TemplateForm = {
  code: '',
  title: '',
  description: '',
  category: 'ONBOARDING',
  kind: 'FORM',
  assigneeType: 'ORG',
  assigneeRoleKey: '',
  priority: 'CRITICAL',
  actionKey: '',
  sortOrder: 0,
  isActive: true,
};

function toForm(row: TaskTemplate): TemplateForm {
  return {
    id: row.id,
    code: row.code,
    title: row.title,
    description: row.description ?? '',
    category: row.category,
    kind: row.kind,
    assigneeType: row.assigneeType,
    assigneeRoleKey: row.assigneeRoleKey ?? '',
    priority: row.priority,
    actionKey: row.actionKey,
    sortOrder: row.sortOrder,
    isActive: row.isActive,
  };
}

export function TasksPage() {
  const [category, setCategory] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState<TemplateForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError, isFetching } = useGetTaskTemplatesQuery(
    category ? { category } : undefined,
  );
  const [upsertTemplate, upsertState] = useUpsertTaskTemplateMutation();

  const templates = data?.taskTemplates ?? [];
  const rows = useMemo(() => templates, [templates]);
  const isEdit = Boolean(form.id);

  function openCreate() {
    setForm(EMPTY_FORM);
    setFormError(null);
    setDrawerOpen(true);
  }

  function openEdit(row: TaskTemplate) {
    setForm(toForm(row));
    setFormError(null);
    setDrawerOpen(true);
  }

  async function toggleActive(row: TaskTemplate) {
    await upsertTemplate({
      id: row.id,
      code: row.code,
      title: row.title,
      description: row.description ?? undefined,
      category: row.category,
      kind: row.kind,
      assigneeType: row.assigneeType,
      assigneeRoleKey: row.assigneeRoleKey,
      priority: row.priority,
      actionKey: row.actionKey,
      sortOrder: row.sortOrder,
      isActive: !row.isActive,
    }).unwrap();
  }

  async function handleSave() {
    setFormError(null);
    if (!form.code.trim() || !form.title.trim() || !form.actionKey.trim()) {
      setFormError('Code, title, and action key are required.');
      return;
    }
    try {
      await upsertTemplate({
        id: form.id,
        code: form.code.trim().toUpperCase(),
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        category: form.category,
        kind: form.kind,
        assigneeType: form.assigneeType,
        assigneeRoleKey:
          form.assigneeType === 'ROLE' ? form.assigneeRoleKey.trim() || undefined : undefined,
        priority: form.priority,
        actionKey: form.actionKey.trim(),
        sortOrder: form.sortOrder,
        isActive: form.isActive,
      }).unwrap();
      setDrawerOpen(false);
      setForm(EMPTY_FORM);
    } catch {
      setFormError('Could not save template. Check code uniqueness and try again.');
    }
  }

  if (isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Task templates"
        description="Onboarding and future approval task definitions assigned when orgs and staff are created."
        actions={
          <Group gap="sm">
            <Select
              placeholder="All categories"
              clearable
              w={180}
              value={category}
              onChange={setCategory}
              data={[
                { value: 'ONBOARDING', label: 'Onboarding' },
                { value: 'APPROVAL', label: 'Approval' },
                { value: 'GENERAL', label: 'General' },
              ]}
            />
            <Button onClick={openCreate}>Add template</Button>
          </Group>
        }
      />

      {isError ? (
        <Alert color="red" title="Failed to load templates">
          Could not load task templates. Ensure the API is running and task tables exist (run seed or
          enable TypeORM sync).
        </Alert>
      ) : null}

      {!isError && rows.length === 0 ? (
        <Stack align="center" gap="md" py="xl">
          <EmptyState
            title="No templates"
            description="Create your first task template, or run the database seed for default onboarding templates."
          />
          <Button onClick={openCreate}>Add template</Button>
        </Stack>
      ) : !isError ? (
        <DataTable
          columns={[
            { key: 'id', header: 'ID', render: (r: TaskTemplate) => r.id },
            { key: 'code', header: 'Code', render: (r: TaskTemplate) => r.code },
            { key: 'title', header: 'Title', render: (r: TaskTemplate) => r.title },
            {
              key: 'category',
              header: 'Category',
              render: (r: TaskTemplate) => <Badge variant="light">{r.category}</Badge>,
            },
            {
              key: 'kind',
              header: 'Kind',
              render: (r: TaskTemplate) => r.kind,
            },
            {
              key: 'assignee',
              header: 'Assignee',
              render: (r: TaskTemplate) => r.assigneeType,
            },
            {
              key: 'priority',
              header: 'Priority',
              render: (r: TaskTemplate) => (
                <Badge color={r.priority === 'CRITICAL' ? 'red' : 'gray'}>{r.priority}</Badge>
              ),
            },
            {
              key: 'active',
              header: 'Active',
              render: (r: TaskTemplate) => (
                <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={r.isActive}
                    disabled={upsertState.isLoading}
                    onChange={() => void toggleActive(r)}
                  />
                  <Text size="sm" c="dimmed">
                    {r.isActive ? 'On' : 'Off'}
                  </Text>
                </Group>
              ),
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (r: TaskTemplate) => (
                <Button
                  size="xs"
                  variant="default"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(r);
                  }}
                >
                  Edit
                </Button>
              ),
            },
          ]}
          rows={rows}
          getRowId={(r) => r.id}
          onRowClick={openEdit}
        />
      ) : null}

      <BottomDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={isEdit ? 'Edit task template' : 'Add task template'}
      >
        <Stack gap="md">
          {formError ? <Alert color="red" title={formError} /> : null}

          <FormField label="Code" required>
            <TextInput
              placeholder="ORG_ADDRESS"
              value={form.code}
              disabled={isEdit}
              onChange={(e) => {
                const code = e.currentTarget.value;
                setForm((f) => ({ ...f, code }));
              }}
            />
          </FormField>

          <FormField label="Title" required>
            <TextInput
              value={form.title}
              onChange={(e) => {
                const title = e.currentTarget.value;
                setForm((f) => ({ ...f, title }));
              }}
            />
          </FormField>

          <FormField label="Description">
            <Textarea
              minRows={2}
              value={form.description}
              onChange={(e) => {
                const description = e.currentTarget.value;
                setForm((f) => ({ ...f, description }));
              }}
            />
          </FormField>

          <FormField label="Category" required>
            <Select
              value={form.category}
              onChange={(value) => setForm((f) => ({ ...f, category: value || 'ONBOARDING' }))}
              data={[
                { value: 'ONBOARDING', label: 'Onboarding' },
                { value: 'APPROVAL', label: 'Approval' },
                { value: 'GENERAL', label: 'General' },
              ]}
              allowDeselect={false}
            />
          </FormField>

          <FormField label="Kind" required>
            <Select
              value={form.kind}
              onChange={(value) => setForm((f) => ({ ...f, kind: value || 'FORM' }))}
              data={[
                { value: 'FORM', label: 'Form' },
                { value: 'APPROVAL', label: 'Approval' },
              ]}
              allowDeselect={false}
            />
          </FormField>

          <FormField label="Assignee type" required>
            <Select
              value={form.assigneeType}
              onChange={(value) => setForm((f) => ({ ...f, assigneeType: value || 'ORG' }))}
              data={[
                { value: 'ORG', label: 'Organization' },
                { value: 'STAFF', label: 'Staff' },
                { value: 'ROLE', label: 'Role' },
                { value: 'USER', label: 'User' },
              ]}
              allowDeselect={false}
            />
          </FormField>

          {form.assigneeType === 'ROLE' ? (
            <FormField label="Assignee role key">
              <TextInput
                placeholder="procurement.approve"
                value={form.assigneeRoleKey}
                onChange={(e) => {
                  const assigneeRoleKey = e.currentTarget.value;
                  setForm((f) => ({ ...f, assigneeRoleKey }));
                }}
              />
            </FormField>
          ) : null}

          <FormField label="Priority" required>
            <Select
              value={form.priority}
              onChange={(value) => setForm((f) => ({ ...f, priority: value || 'NORMAL' }))}
              data={[
                { value: 'CRITICAL', label: 'Critical' },
                { value: 'HIGH', label: 'High' },
                { value: 'NORMAL', label: 'Normal' },
                { value: 'LOW', label: 'Low' },
              ]}
              allowDeselect={false}
            />
          </FormField>

          <FormField label="Action key" required>
            <TextInput
              placeholder="address"
              value={form.actionKey}
              onChange={(e) => {
                const actionKey = e.currentTarget.value;
                setForm((f) => ({ ...f, actionKey }));
              }}
            />
          </FormField>

          <FormField label="Sort order">
            <NumberInput
              value={form.sortOrder}
              onChange={(value) =>
                setForm((f) => ({ ...f, sortOrder: typeof value === 'number' ? value : 0 }))
              }
            />
          </FormField>

          <Group gap="sm">
            <Switch
              checked={form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.currentTarget.checked }))}
              label="Active"
            />
          </Group>

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDrawerOpen(false)}>
              Cancel
            </Button>
            <Button loading={upsertState.isLoading} onClick={() => void handleSave()}>
              {isEdit ? 'Save changes' : 'Create template'}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
